import * as React from 'react'
import Link from 'next/link'
import useSWR from 'swr'
import { FormattedMessage, useIntl } from 'react-intl'
import { format } from 'date-fns'
import Client from 'clients/incidents'
import { Page } from 'layouts/pages'
import { Main, Content, Header } from 'components/page'
import { Br } from 'components/misc'
import { Responsive } from 'components/table'
import Pagination from 'components/pagination'
import { Metadata, Entry as MetadataEntry } from 'components/metadata'
import Shim from 'components/Shim'
import { DropdownFromOptions } from 'components/controls'
import { useIntlMemo } from 'hooks/intl'
import * as Texts from 'components/text'
import * as Async from 'contexts/async'
import Head from 'components/Head'
import styles from './incidents.module.css'

export async function getStaticProps() {
    return {
        props: {
            incidents: await Client.get(),
        },
        revalidate: 300, // in seconds, 5 minutes
    }
}

export default function IncidentsList({ incidents }) {
    const [page, setPage] = React.useState(1)
    const [filters, setFilters] = React.useState({})
    const from = filters.from ? seasonToFrom(filters.from) : undefined
    const to = filters.to ? seasonToTo(filters.to) : undefined

    function handlerFiltersChange(filters) {
        setFilters(filters)
        setPage(1)
    }

    return (
        <Async.Provider value={useIncidents(page, from, to, incidents)}>
            <Filters values={filters} onChange={handlerFiltersChange} />
            <Async.Pending>
                <Texts.Loading>
                    <FormattedMessage
                        description="Layout incidents/layouts"
                        defaultMessage="Loading incidents..."
                    />
                </Texts.Loading>
            </Async.Pending>
            <Async.Found>
                {payload => (
                    <React.Fragment>
                        <Br />
                        <Table incidents={payload.results} />
                        <Shim vertical>
                            <Pagination
                                total={payload.count / 50}
                                active={page}
                                onChange={setPage}
                            />
                        </Shim>
                    </React.Fragment>
                )}
            </Async.Found>
            <Async.Error>
                <Texts.Error>
                    <FormattedMessage
                        defaultMessage="An erroroccured while loading incidents."
                        description="Layout incidents/layouts"
                    />
                </Texts.Error>
            </Async.Error>
        </Async.Provider>
    )
}

export function getLayout(page) {
    return (
        <Page>
            <PageHead />
            <Content>
                <Main>{page}</Main>
            </Content>
        </Page>
    )
}

function PageHead() {
    const intl = useIntl()
    const title = intl.formatMessage({
        defaultMessage: 'Historical Incidents',
    })

    return (
        <React.Fragment>
            <Head title={title} description={title} />
            <Header title={title} />
        </React.Fragment>
    )
}

IncidentsList.getLayout = getLayout

function useIncidents(page, from, to, fallbackData) {
    return useSWR([page, from, to], () => Client.get({ page, from, to }), { fallbackData })
}

function Table({ incidents }) {
    return (
        <Responsive>
            <table>
                <thead>
                    <tr>
                        <th>
                            <FormattedMessage
                                description="Layout incidents/layout"
                                defaultMessage="Date"
                            />
                        </th>
                        <th>
                            <FormattedMessage
                                description="Layout incidents/layout"
                                defaultMessage="Location"
                            />
                        </th>
                        <th>
                            <FormattedMessage
                                description="Layout incidents/layout"
                                defaultMessage="Province"
                            />
                        </th>
                        <th>
                            <FormattedMessage
                                description="Layout incidents/layout"
                                defaultMessage="Activity"
                            />
                        </th>
                        <th>
                            <FormattedMessage
                                description="Layout incidents/layout"
                                defaultMessage="Involvement"
                            />
                        </th>
                        <th>
                            <FormattedMessage
                                description="Layout incidents/layout"
                                defaultMessage="Injury"
                            />
                        </th>
                        <th>
                            <FormattedMessage
                                description="Layout incidents/layout"
                                defaultMessage="Fatal"
                            />
                        </th>
                        <th />
                    </tr>
                </thead>
                <tbody>
                    {incidents.map(props => (
                        <tr key={props.id}>
                            <td>
                                <span className={styles.DateCell}>{props.date}</span>
                            </td>
                            <td>{props.location}</td>
                            <Cell>{props.location_province}</Cell>
                            <td>{props.group_activity}</td>
                            <Cell>{props.num_involved}</Cell>
                            <Cell>{props.num_injured}</Cell>
                            <Cell>{props.num_fatal}</Cell>
                            <td>
                                <Link href={'/incidents/' + props.id}>
                                    <a>
                                        <FormattedMessage
                                            description="Layout incidents/layout"
                                            defaultMessage="view"
                                        />
                                    </a>
                                </Link>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </Responsive>
    )
}

function Filters({ onChange, values }) {
    const filters = useFilters()
    const { from, to } = values
    const froms = React.useMemo(() => {
        // Remove last filter year ( most recent ) as it results in an empty "TO" list and no elements
        // Validate this works when there are entries for this year
        const f = filters.slice(0, filters.length - 1)

        return new Map(f.filter(f => startsBefore(f, to)).map(f => [f.season, f.title]))
    }, [to])
    const tos = React.useMemo(
        () => new Map(filters.filter(f => startsAfter(f, from)).map(f => [f.season, f.title])),

        [from]
    )
    const intl = useIntl()
    function handleChangeFrom(from) {
        onChange({ from, to })
    }
    function handleChangeTo(to) {
        onChange({ from, to })
    }

    return (
        <Metadata>
            <MetadataEntry
                term={intl.formatMessage({
                    defaultMessage: 'From',
                    description: 'Layout incidents/layouts',
                })}
                horizontal>
                <DropdownFromOptions
                    onChange={handleChangeFrom}
                    value={from}
                    placeholder={intl.formatMessage({
                        defaultMessage: 'From',
                        description: 'Layout incidents/layouts',
                    })}
                    options={froms}
                />
            </MetadataEntry>
            <MetadataEntry
                term={intl.formatMessage({
                    defaultMessage: 'To',
                    description: 'Layout incidents/layouts',
                })}
                horizontal>
                <DropdownFromOptions
                    onChange={handleChangeTo}
                    value={to}
                    placeholder={intl.formatMessage({
                        defaultMessage: 'To',
                        description: 'Layout incidents/layouts',
                    })}
                    options={tos}
                />
            </MetadataEntry>
        </Metadata>
    )
}

export function Cell({ children, suffix }) {
    if (typeof children === 'number') {
        children = children.toString()
    }

    return <td>{children ? [children, suffix].filter(Boolean).join(' ') : '-'}</td>
}

// Utils
/*
 * TODO(wnh): This is SOOOO gross. I feel dirty.
 */
function seasonToFrom(season) {
    if (season === 1980) {
        return '0001-01-01'
    } else {
        return format(new Date(season, 5, 1), 'YYYY-MM-DD')
    }
}
function seasonToTo(season) {
    return format(new Date(season + 1, 4, 30), 'YYYY-MM-DD')
}
function startsBefore(filterVal, currentToVal) {
    // If the current set from value is null or undefined show everything
    if (!currentToVal) {
        return true
    } else {
        return filterVal.season < currentToVal
    }
}
function startsAfter(filterVal, currentFromVal) {
    // If the current set from value is null or undefined show everything
    if (!currentFromVal) {
        return true
    } else {
        return filterVal.season > currentFromVal
    }
}

/*
 * TODO(wnh): clean this up
 * TODO(wnh): do some more (any?) testing
 */

function useFilters() {
    return useIntlMemo(intl => {
        var start = 1981
        var now = new Date()
        var end = now.getFullYear()

        if (now.getMonth() < 6) {
            end = end - 1
        }

        var filters = []

        filters.push({
            title: intl.formatMessage({
                defaultMessage: '1980/1981 and earlier',
                description: 'Layout incidents/layouts',
            }),
            season: 1980,
            start: '0000-01-01',
            end: '1980-06-30',
        })

        for (var i = start; i <= end; i++) {
            const f = {
                title: String(i) + '/' + String(i + 1),
                season: i,
                start: format(new Date(i, 5, 1), 'YYYY-MM-DD'),
                end: format(new Date(i + 1, 4, 30), 'YYYY-MM-DD'),
            }
            filters.push(f)
        }
        return filters
    })
}

export function latLng(coords) {
    if (!Array.isArray(coords)) {
        return '-'
    }

    return (
        coords
            .filter(Boolean)
            .map(Number)
            .map(coord => `${coord.toFixed(5)} °`)
            .join(', ') || '-'
    )
}
